import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { startFreeTrial } from "@/api/subscriptions";
import { useExamStore } from "@/stores/examStore";
import type { ExamDetail } from "@/types";

export default function FreeTrialStart() {
  const navigate = useNavigate();
  const { startExam } = useExamStore();
  const queryClient = useQueryClient();

  const freeTrialMutation = useMutation({
    mutationFn: startFreeTrial,
    onSuccess: (data: any) => {
      // Invalidate so results page sees has_used_free_trial=true immediately
      queryClient.invalidateQueries({ queryKey: ["access-status"] });
      const examDetail: ExamDetail = {
        ...data.exam,
        questions: data.questions,
      };
      startExam(examDetail, data.attempt_id);
      navigate(`/exams/${data.exam.id}/take`, { replace: true });
    },
  });

  return (
    <div className="page-enter mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Free Trial</p>
            <h1 className="text-2xl font-bold text-gray-900">Mock Theory Test</h1>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-primary-50 p-4 text-center">
            <div className="text-3xl font-bold text-primary-700">50</div>
            <div className="text-sm text-primary-600 mt-1">Questions</div>
          </div>
          <div className="rounded-lg bg-primary-50 p-4 text-center">
            <div className="text-3xl font-bold text-primary-700">57</div>
            <div className="text-sm text-primary-600 mt-1">Minutes</div>
          </div>
          <div className="rounded-lg bg-success-50 p-4 text-center">
            <div className="text-3xl font-bold text-success-700">43/50</div>
            <div className="text-sm text-success-600 mt-1">Pass Mark</div>
          </div>
        </div>

        {/* Rules */}
        <div className="mt-8 rounded-lg bg-gray-50 p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Before you start:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>You will have 57 minutes to complete all 50 questions</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Each question has 4 possible answers — select the one you believe is correct</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>You can navigate between questions and change your answers at any time</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>You need at least 43 correct answers to pass (86%)</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Some questions may include images or video clips</span>
            </li>
            <li className="flex items-start space-x-2">
              <svg className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>This is your one free trial — you can only take it once</span>
            </li>
          </ul>
        </div>

        {freeTrialMutation.isError && (
          <p className="mt-4 text-center text-sm text-danger-600">
            Failed to start exam. Please try again.
          </p>
        )}

        <div className="mt-8 flex justify-center">
          <button
            onClick={() => freeTrialMutation.mutate(undefined)}
            disabled={freeTrialMutation.isPending}
            className="rounded-lg bg-primary-600 px-10 py-3 text-base font-semibold text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {freeTrialMutation.isPending ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Starting…</span>
              </span>
            ) : (
              "Start Free Trial Exam"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
